/**
 * Resilient formatter for backend payload validation errors.
 *
 * The backend's `/validate` endpoint returns errors as
 * `Array<Record<string, unknown>>` — the actual key names vary across
 * libraries (AJV, Spring/Jakarta, custom). This helper picks a readable
 * line from any plausible shape so the toast never collapses to "• • •".
 */

const PATH_KEYS = [
  "path",
  "pointer",
  "jsonPath",
  "field",
  "fieldPath",
  "loc",
  "location",
  "instancePath",
  "schemaPath",
] as const;

const MESSAGE_KEYS = [
  "message",
  "msg",
  "error",
  "detail",
  "description",
  "reason",
  "text",
  "defaultMessage",
] as const;

function pickString(obj: Record<string, unknown>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (Array.isArray(v) && v.length > 0) {
      const joined = v
        .map((part) => (typeof part === "string" || typeof part === "number" ? String(part) : ""))
        .filter(Boolean)
        .join(".");
      if (joined) return joined;
    }
  }
  return "";
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

export function formatValidationError(e: unknown): string {
  if (e == null) return "";
  if (typeof e === "string") return e.trim();
  if (typeof e !== "object") return String(e);

  const obj = e as Record<string, unknown>;
  const path = pickString(obj, PATH_KEYS);
  const message = pickString(obj, MESSAGE_KEYS);

  if (path && message) return `${path} ${message}`;
  if (message) return message;
  if (path) return path;

  // Last resort: stringify so the user (and we) can see *something*.
  try {
    return truncate(JSON.stringify(e), 140);
  } catch {
    return "";
  }
}

export function formatValidationErrors(errs: unknown[], max = 4): string {
  if (!Array.isArray(errs) || errs.length === 0) return "Validation failed.";
  const lines = errs.slice(0, max).map(formatValidationError).filter(Boolean);
  if (lines.length === 0) return "Validation failed.";
  return lines.join(" • ");
}
